import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Wallet, Calendar, DollarSign, Save, Info, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const fmt = v => Number(v || 0).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' });

export const ConfigurationSoldes = () => {
  const [checkpoints, setCheckpoints] = useState([]);
  const [soldeCash, setSoldeCash] = useState("");
  const [dateCash, setDateCash] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [savingCash, setSavingCash] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [newMontant, setNewMontant] = useState("");
  const [savingNew, setSavingNew] = useState(false);

  useEffect(() => { loadSoldes(); }, []);

  const loadSoldes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/soldes-initiaux`);
      if (response.status !== 200) { toast.error("Erreur de chargement"); return; }
      const data = response.data;
      setCheckpoints(data.bancaires_multiples || []);
      if (data.cash) {
        setSoldeCash(String(data.cash.montant ?? ""));
        setDateCash(data.cash.date || new Date().toISOString().split('T')[0]);
      }
    } catch { toast.error("Impossible de charger les soldes"); }
    finally { setLoading(false); }
  };

  const saveCheckpoint = async () => {
    const montant = parseFloat(newMontant);
    if (isNaN(montant) || !newDate) { toast.error("Date et montant requis"); return; }
    setSavingNew(true);
    try {
      const r = await axios.post(`${API_URL}/api/soldes-initiaux/bancaire`, { montant, date: newDate });
      if (r.status === 200) {
        toast.success("Checkpoint enregistré!");
        setNewDate(""); setNewMontant("");
        loadSoldes();
      } else toast.error("Erreur enregistrement");
    } catch { toast.error("Erreur réseau"); }
    finally { setSavingNew(false); }
  };

  const deleteCheckpoint = async (date) => {
    if (!confirm(`Supprimer le checkpoint du ${date}?`)) return;
    try {
      const r = await axios.delete(`${API_URL}/api/soldes-initiaux/bancaire?date=${date}`);
      if (r.status === 200) { toast.success("Supprimé"); loadSoldes(); }
      else toast.error("Erreur suppression");
    } catch { toast.error("Erreur réseau"); }
  };

  const saveSoldeCash = async () => {
    const montant = parseFloat(soldeCash);
    if (isNaN(montant) || !dateCash) { toast.error("Montant et date requis"); return; }
    setSavingCash(true);
    try {
      const r = await axios.post(`${API_URL}/api/soldes-initiaux/cash`, { montant, date: dateCash });
      if (r.status === 200) toast.success("Solde cash enregistré!");
      else toast.error("Erreur");
    } catch { toast.error("Erreur réseau"); }
    finally { setSavingCash(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64">Chargement...</div>;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="config-soldes-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Configuration des Soldes Initiaux</h1>
        <p className="text-slate-500 mt-1 text-sm">Points de contrôle pour le calcul de trésorerie</p>
      </div>

      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-900 text-sm">
          Ajoutez un solde bancaire pour chaque point de contrôle (ex: 01/01/2024, 01/01/2025, 01/01/2026). 
          Le système utilisera le checkpoint le plus récent avant chaque mois pour calculer le cashflow.
        </AlertDescription>
      </Alert>

      {/* Checkpoints bancaires */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Wallet className="w-5 h-5 text-blue-600" />
            Soldes Bancaires — Points de contrôle
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Liste des checkpoints existants */}
          {checkpoints.length > 0 && (
            <div className="space-y-2">
              {checkpoints.map((cp, i) => (
                <div key={cp.date} className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-blue-700">
                      <Calendar className="w-4 h-4" />
                      <span className="font-medium">{cp.date}</span>
                    </div>
                    <span className="text-lg font-bold text-blue-900">{fmt(cp.montant)}</span>
                  </div>
                  <button
                    onClick={() => deleteCheckpoint(cp.date)}
                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Supprimer"
                    data-testid={`delete-checkpoint-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Ajouter un nouveau checkpoint */}
          <div className="border-t pt-4 mt-4">
            <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4" /> Ajouter un point de contrôle
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} data-testid="new-checkpoint-date" />
              </div>
              <div>
                <Label className="text-xs">Solde bancaire ($)</Label>
                <Input type="number" step="0.01" value={newMontant} onChange={e => setNewMontant(e.target.value)} placeholder="45000.00" data-testid="new-checkpoint-montant" />
              </div>
              <Button onClick={saveCheckpoint} disabled={savingNew || !newDate || !newMontant} className="bg-blue-600 hover:bg-blue-700" data-testid="save-checkpoint-btn">
                <Save className="w-4 h-4 mr-2" />
                {savingNew ? "..." : "Enregistrer"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Solde Cash */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Solde Cash Initial
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-xs">Date de référence</Label>
              <Input type="date" value={dateCash} onChange={e => setDateCash(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Solde cash ($)</Label>
              <Input type="number" step="0.01" value={soldeCash} onChange={e => setSoldeCash(e.target.value)} placeholder="5000.00" />
            </div>
            <Button onClick={saveSoldeCash} disabled={savingCash || !soldeCash || !dateCash} className="bg-emerald-600 hover:bg-emerald-700">
              <Save className="w-4 h-4 mr-2" />
              {savingCash ? "..." : "Enregistrer"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfigurationSoldes;
